// ABOUTME: Modal for splitting a picking line across multiple stocks
// ABOUTME: First row is locked to the original location, additional rows pick from the remaining stocks

import AddIcon from '@mui/icons-material/Add';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CloseIcon from '@mui/icons-material/Close';
import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateOrderPickingLineMutation } from "../../../newapi/order/ordersSlice";
import { tokens } from "../../../theme";
import { showToastMessageRequestError, showToastMessageUpdateRequest } from "../../../utils/toasts";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import Shbutton from "../../theme/buttons/Shbutton";
import { InputDropdown } from "../../theme/dropdowns/InputDropdown";
import TextInput from "../../theme/inputs/TextInput";
import SHModal from "../../theme/Modal/SHModal";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";

const MSplitPickingLine = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const pickingLine = props.pickingLine;
    const totalAmount = pickingLine?.amount ?? 0;
    const originalStock = pickingLine?.stock;
    const originalStockId = originalStock?.id;
    const allStocks = pickingLine?.stock?.product?.stocks ?? [];

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    // Splits: array of {stockId, amount}. Index 0 is always the original stock.
    const [splits, setSplits] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (props.open && originalStockId) {
            setSplits([
                { stockId: originalStockId, amount: totalAmount },
                { stockId: null, amount: 0 },
            ]);
            setError(null);
        }
    }, [props.open, originalStockId, totalAmount]);

    const totalAssigned = splits.reduce((sum, s) => sum + (parseInt(s.amount) || 0), 0);
    const remaining = totalAmount - totalAssigned;

    const updateAmount = (index, value) => {
        const next = [...splits];
        const parsed = value === '' ? 0 : parseInt(value);
        next[index] = { ...next[index], amount: isNaN(parsed) ? 0 : parsed };
        setSplits(next);
    };

    const updateStockId = (index, stockId) => {
        const next = [...splits];
        next[index] = { ...next[index], stockId };
        setSplits(next);
    };

    const addRow = () => {
        setSplits([...splits, { stockId: null, amount: 0 }]);
    };

    const removeRow = (index) => {
        if (index === 0) return;
        setSplits(splits.filter((_, i) => i !== index));
    };

    // Exclude stocks already chosen in other rows so each stock appears once
    const optionsForRow = (rowIndex) => {
        const usedStockIds = splits
            .map((s, i) => (i !== rowIndex ? s.stockId : null))
            .filter(Boolean);
        return allStocks.filter(stock => !usedStockIds.includes(stock.id));
    };

    const canAddRow = splits.length < allStocks.length;

    const [updatePickingLine, { isLoading }] = useUpdateOrderPickingLineMutation();

    const handleSave = async () => {
        setError(null);

        const cleaned = splits.filter(s => s.stockId && s.amount > 0);

        if (cleaned.length === 0) {
            setError(t("split_pickingline_no_rows"));
            return;
        }

        const sum = cleaned.reduce((acc, s) => acc + s.amount, 0);
        if (sum !== totalAmount) {
            setError(t("split_pickingline_amount_mismatch"));
            return;
        }

        try {
            const response = await updatePickingLine({
                id: pickingLine.id,
                orderId: pickingLine.orderId,
                stocks: cleaned.map(s => ({ id: s.stockId, amount: s.amount })),
            }).unwrap();
            showToastMessageUpdateRequest(t, response);
            props.handleClose();
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    };

    return (
        <SHModal
            open={props.open}
            onClose={props.handleClose}
            sx={{ body: { minWidth: 500, maxWidth: 700 } }}
        >
            <Box sx={{
                textAlign: "center",
                paddingY: 3,
                paddingX: 3,
                display: "flex",
                flexDirection: "column",
                alignItems:"center",
            }}>
                <Subheading>{t("split_pickingline")}</Subheading>
                <Text center light>
                    {t("split_pickingline_subtitle")}
                </Text>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200],
                        minHeight: 32,
                        pl: 2,
                        pr: 0.5,
                        gap: 1,
                        borderRadius: 7,
                        width: 1,
                        mt:2,
                    }}
                >

                    <Text bold>
                        {t("too_divide")}
                    </Text>
                    <Box
                        sx={{
                            height:24,
                            minWidth:24,
                            px:1,
                            background:theme.palette.mode === "dark" ? colors.grey[700] : colors.grey[0],
                            borderRadius:3,
                        }}
                    >
                        <Text>{remaining}</Text>
                    </Box>
                </Box>
            </Box>

            <Box sx={{
                background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                padding: 3,
                pb: 0,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                display: "flex",
                width: 1,
                flexDirection: "column",
                maxHeight: 600,
                overflow: "auto"
            }}>
                {error && (
                    <Box
                        sx={{
                            height:32,
                            px:2,
                            background:colors.red[100],
                            border:`1px solid ${colors.red[400]}`,
                            mb:2,
                            borderRadius:4,
                        }}
                    >
                        <Text variant="red">{error}</Text>
                    </Box>
                )}
                <Box
                    sx={{
                        display:'flex',
                        flexDirection:"column",
                        gap:1,
                        mb:1.5,
                    }}
                >
                    {splits.map((split, index) => (
                        <SplitPickingLineRow
                            key={index}
                            index={index}
                            split={split}
                            isFirst={index === 0}
                            originalStock={originalStock}
                            options={optionsForRow(index)}
                            onAmountChange={(value) => updateAmount(index, value)}
                            onStockChange={(stockId) => updateStockId(index, stockId)}
                            onRemove={() => removeRow(index)}
                        />
                    ))}
                </Box>

                <Shbutton
                    color={"primaryGrey"}
                    variant="contained"
                    block
                    startIcon={<AddIcon />}
                    onClick={addRow}
                    disabled={!canAddRow}
                >
                    {t("split_pickingline_add_row")}
                </Shbutton>

            </Box>

            <Box component="footer" sx={{
                display: "flex",
                width: 1,
                paddingBottom: isStandAlone ? "32px" : "16px",
                alignItems: "center",
                justifyContent: "space-between",
                paddingRight: 3,
                zIndex: 1058,
                position: "relative",
                paddingTop: 2,
                background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                borderBottomLeftRadius: 24,
                borderBottomRightRadius: 24,
                "@media screen and (max-width: 47.9375em)": {
                    paddingRight: 0,
                }
            }}>
                <Shbutton
                    isCancel={true}
                    className={"always-visible"}
                    onClick={props.handleClose}
                    color="primary"
                    variant="text"
                    startIcon={<ArrowRightAltIcon />}
                >
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        className={"group-btn"}
                        color={"primary"}
                        variant="contained"
                        isLoading={isLoading}
                        onClick={handleSave}
                    >
                        {t("save")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

const SplitPickingLineRow = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const selectedIndex = props.split.stockId
        ? props.options.findIndex(stock => stock.id === props.split.stockId)
        : -1;

    return (
        <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
            border: `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
            boxShadow: "0px 1px 2px rgba(49,65,95,.55)",
            height: 40,
            pl: props.isFirst ? 2 : 0.5,
            pr: 0.5,
            // py: 1,
            gap: 1,
            borderRadius: 5,
            width: 1,
        }}>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
                {props.isFirst ? (
                    <Box

                    >
                        <Text bold>{props.originalStock?.warehouseLocation?.location ?? "-"}</Text>
                    </Box>
                ) : (
                    <InputDropdown
                        displayName={"warehouseLocation"}
                        options={props.options}
                        selected={selectedIndex}
                        noSelection={t("select_location")}
                        onChange={(index) => props.onStockChange(props.options[index]?.id ?? null)}
                        closeOnSelection
                        stopPropagation
                    />
                )}
            </Box>

            <TextInput
                type="number"
                width={64}
                value={props.split.amount || ''}
                onChange={(e) => props.onAmountChange(e.target.value)}
            />

            {!props.isFirst ? (
                <Box
                    onClick={props.onRemove}
                    sx={{
                        width: 32,
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        color: colors.red[400],
                        borderRadius: 4,
                        "&:hover": {
                            background:colors.red[100],
                        }
                    }}
                >
                    <CloseIcon fontSize="small" />
                </Box>
            ) : (
                <Box sx={{ width: 32, flexShrink: 0 }} />
            )}
        </Box>
    );
};

export default MSplitPickingLine;
